import i18n from '../config/i18n';
import { getPreferredApiLocale } from './locale';

export interface ApiErrorResponse {
  code: string;
  message: string;
  correlationId?: string;
}

export class AppError extends Error {
  readonly code?: string;
  readonly correlationId?: string;

  constructor(message: string, code?: string, correlationId?: string) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.correlationId = correlationId;
  }
}

const AUTH_TOKEN_KEY = 'loopcast_token';

export function getStoredToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (token == null) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } else {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
}

function genericErrorMessage(): string {
  const lng = i18n.language || 'tr';
  const msg =
    i18n.getResource(lng, 'translation', 'request.genericError') ??
    i18n.getResource('tr', 'translation', 'request.genericError');
  return typeof msg === 'string' ? msg : 'Beklenmeyen bir hata oluştu.';
}

function isApiErrorBody(data: unknown): data is ApiErrorResponse {
  if (!data || typeof data !== 'object') return false;
  const body = data as Partial<ApiErrorResponse>;
  return typeof body.code === 'string' && body.code.startsWith('ERR_') && typeof body.message === 'string' && body.message.length > 0;
}

async function throwOnNotOk(response: Response): Promise<never> {
  let code = 'ERR_INTERNAL';
  let message = genericErrorMessage();
  let correlationId: string | undefined;
  try {
    const text = await response.text();
    if (text.trim()) {
      const body = JSON.parse(text) as ApiErrorResponse;
      if (body.code) code = body.code;
      if (body.message) message = body.message;
      if (body.correlationId) correlationId = body.correlationId;
    }
  } catch {
    // ignore parse errors, keep defaults
  }
  const isCredentialAuth = /\/auth\/(login|register)(?:\?|$)/.test(response.url);
  if (response.status === 401 && !isCredentialAuth) {
    setStoredToken(null);
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  }
  throw new AppError(message, code, correlationId);
}

/** fetch wrapper: attaches Bearer token + Accept-Language, parses JSON, throws AppError on non-2xx. */
export async function apiRequest<T = unknown>(url: string, init?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  headers.set('Accept-Language', getPreferredApiLocale());
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) await throwOnNotOk(res);
  const text = await res.text();
  if (!text.trim()) return null as T;
  const data = JSON.parse(text) as unknown;
  // Defensive: DomainError once leaked as HTTP 200 + { code, message }.
  if (isApiErrorBody(data)) {
    throw new AppError(data.message, data.code, data.correlationId);
  }
  return data as T;
}
